import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Coins, Loader2, ExternalLink, Check, Plus, History } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { withBackend } from '../utils/api';
import TeamPage from './TeamPage';

/**
 * Billing: plan, credit balance, plan switching and credit top-ups.
 *
 * Renders NOTHING when billing is switched off on the backend (no Stripe
 * key). That is deliberate: an unconfigured deploy should look exactly as
 * it did before billing existed rather than showing a dead panel.
 *
 * Everything shown here comes from ONE /api/billing/summary call, including
 * the credit price list, so the "what will this cost" table needs no extra
 * round trip.
 */

const money = (cents, currency = 'usd') => {
  if (cents === null || cents === undefined) return '';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: (currency || 'usd').toUpperCase(),
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
};

const num = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US'));

const TYPE_LABELS = {
  presentation: 'Presentation slide',
  infographic: 'Infographic',
  document: 'Document',
  dashboard: 'Dashboard',
  sheet: 'Sheet',
};
const TIER_ORDER = ['flash', 'pro', 'opus', 'ultra']; // r178: ultra = GPT-6 Astra
const TIER_LABELS = { flash: 'Fast', pro: 'Pro', opus: 'Premium', ultra: 'Ultra' };

export default function BillingPanel({ userId, email }) {
  const [summary, setSummary] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState('month'); // r150
  const [busy, setBusy] = useState('');
  const [showRates, setShowRates] = useState(false);
  const [ledger, setLedger] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const [sRes, pRes] = await Promise.all([
        fetch(withBackend('/api/billing/summary'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId }),
        }),
        fetch(withBackend('/api/billing/plans')),
      ]);
      setSummary(sRes.ok ? await sRes.json() : null);
      setCatalogue(pRes.ok ? await pRes.json() : null);
    } catch (e) {
      // Silent: billing being unreachable must never break the profile page.
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Returning from Stripe Checkout, the subscription arrives by WEBHOOK, so
  // it may land a second or two after the redirect. Re-poll briefly rather
  // than showing the old plan and looking broken.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('checkout') !== 'success') return;
    toast.success('Payment received — applying it to your account…');
    let n = 0;
    const t = setInterval(() => { n += 1; load(); if (n >= 5) clearInterval(t); }, 2000);
    return () => clearInterval(t);
  }, [load]);

  const go = async (path, body, label) => {
    setBusy(label);
    try {
      const res = await fetch(withBackend(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, email, ...body }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || 'Could not start checkout');
      window.location.href = data.url;
    } catch (e) {
      toast.error(String(e.message || e));
      setBusy('');
    }
  };

  const openLedger = async () => {
    if (ledger) { setLedger(null); return; }
    try {
      const res = await fetch(withBackend('/api/billing/ledger'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, limit: 25 }),
      });
      const d = await res.json();
      setLedger(d.entries || []);
    } catch {
      toast.error('Could not load credit history');
    }
  };

  if (loading) {
    return (
      <div className="mt-8 rounded-2xl border border-border bg-card p-6 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading billing…</span>
      </div>
    );
  }

  // Billing not configured on this deploy: no plan UI — but Team must
  // survive (r152, review #3): it lives INSIDE this panel now, so returning
  // null would strand team create/join/invite-redemption entirely.
  if (!summary || !summary.enabled) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-8 rounded-2xl border border-border bg-card p-6"
      >
        <TeamPage userId={userId} embedded />
      </motion.div>
    );
  }

  const { plan, credits, subscription, tier, exempt } = summary;
  const balance = credits?.balance;
  // r152: free divides by the 3-day bank (45), not the monthly display
  // number — the old denominator pinned free meters amber forever.
  const granted = plan?.meter_credits ?? plan?.monthly_credits ?? 0;
  const pct = granted > 0 && balance !== null
    ? Math.max(0, Math.min(100, Math.round((balance / granted) * 100)))
    : null;
  const low = pct !== null && pct <= 15;
  const plans = (catalogue?.plans || []).filter((p) => (p.price_cents || 0) > 0);
  const packs = catalogue?.packs || [];
  const rates = summary.rates || [];
  // r156: the annual toggle appears only when annual is actually BUYABLE
  // (a Stripe price id exists) — during the launch promo annual stays off,
  // a second discount on top of forever-50% makes no sense.
  const anyAnnual = plans.some((p) => p.stripe_price_id_annual);
  // r156: a live subscription is CHANGED in the Stripe portal, never
  // stacked with a second checkout (double billing + the coupon on the
  // first subscription would be lost).
  const hasLiveSub = ['active', 'trialing', 'past_due'].includes(subscription?.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8 rounded-2xl border border-border bg-card p-6"
    >
      <div className="flex items-center gap-2 mb-2">
        <CreditCard className="w-4 h-4 text-emerald-500" />
        <h2 className="text-base font-semibold text-foreground">Plan &amp; credits</h2>
      </div>

      {exempt ? (
        /* Exempt tiers (Prezlab internal, trials) still get the full credits
           UI — an unlimited account deserves a working meter, and "what this
           month's usage WOULD cost" is exactly the number that makes the
           credit rates tunable against real behaviour. */
        <>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">
                  {plan?.display_name || tier}
                </span>
                <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                  Unlimited
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Usage is tracked but never charged on this plan.
              </p>
            </div>
            <div className="text-right">
              <div className="flex items-baseline gap-1.5 justify-end">
                <Coins className="w-4 h-4 text-muted-foreground" />
                <span className="text-2xl font-semibold text-foreground">∞</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {summary.month_usage_credits !== null && summary.month_usage_credits !== undefined
                  ? `${num(summary.month_usage_credits)} credits used this month`
                  : 'credits'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            <Button size="sm" variant="ghost" onClick={() => setShowRates((v) => !v)}>
              {showRates ? 'Hide' : 'What things cost'}
            </Button>
            <Button size="sm" variant="ghost" onClick={openLedger}>
              <History className="w-3.5 h-3.5 mr-1.5" />
              {ledger ? 'Hide history' : 'Credit history'}
            </Button>
          </div>
          {showRates && (
            <div className="mb-2 overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Action</th>
                    {TIER_ORDER.map((t) => (
                      <th key={t} className="text-right font-medium px-3 py-2">{TIER_LABELS[t]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(TYPE_LABELS).map((gt) => (
                    <tr key={gt} className="border-t border-border">
                      <td className="px-3 py-2 text-foreground">
                        {TYPE_LABELS[gt]}
                        {rates.find((r) => r.generation_type === gt)?.per_item && (
                          <span className="text-muted-foreground"> (each)</span>
                        )}
                      </td>
                      {TIER_ORDER.map((mt) => {
                        const r = rates.find((x) => x.generation_type === gt && x.model_tier === mt);
                        return (
                          <td key={mt} className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {r ? num(r.credits) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {ledger && (
            <div className="rounded-xl border border-border divide-y divide-border max-h-56 overflow-y-auto">
              {ledger.length === 0 && (
                <p className="px-3 py-3 text-xs text-muted-foreground">
                  No credit activity — this plan is never charged.
                </p>
              )}
              {ledger.map((e, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                  <span className="text-foreground">{e.reason || e.kind}</span>
                  <span className="tabular-nums font-medium ml-3 shrink-0 text-muted-foreground">
                    {e.delta > 0 ? '+' : ''}{num(e.delta)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {/* ── current plan + balance ── */}
          <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">
                  {plan?.display_name || tier}
                </span>
                {subscription?.status && subscription.status !== 'none' && (
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                    {subscription.status}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {plan?.price_cents ? `${money(plan.price_cents, plan.currency)} / month` : 'No charge'}
                {subscription?.current_period_end && (
                  <>
                    {' · '}
                    {subscription.cancel_at_period_end ? 'ends' : 'renews'}{' '}
                    {new Date(subscription.current_period_end).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </>
                )}
              </p>
            </div>
            <div className="text-right">
              <div className="flex items-baseline gap-1.5 justify-end">
                <Coins className={`w-4 h-4 ${low ? 'text-amber-500' : 'text-muted-foreground'}`} />
                <span className={`text-2xl font-semibold tabular-nums ${low ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'}`}>
                  {num(balance)}
                </span>
                {granted > 0 && (
                  <span className="text-xs text-muted-foreground">/ {num(granted)}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">credits remaining</p>
            </div>
          </div>

          {pct !== null && (
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden mb-1">
              <div
                className={`h-full rounded-full transition-all ${low ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
          {credits?.permanent_balance > 0 && (
            <p className="text-[11px] text-muted-foreground mb-4">
              Includes {num(credits.permanent_balance)} purchased credits, which never expire.
            </p>
          )}
          {summary.month_usage_credits !== null && summary.month_usage_credits !== undefined && (
            <p className="text-[11px] text-muted-foreground mb-1">
              {num(summary.month_usage_credits)} credits used this month.
            </p>
          )}

          <div className="flex flex-wrap gap-2 mb-6 mt-4">
            {subscription?.status === 'active' || subscription?.status === 'past_due' ? (
              <Button
                size="sm" variant="outline"
                disabled={busy === 'portal'}
                onClick={() => go('/api/billing/portal', {}, 'portal')}
              >
                {busy === 'portal'
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <ExternalLink className="w-3.5 h-3.5 mr-1.5" />}
                Manage subscription
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => setShowRates((v) => !v)}>
              {showRates ? 'Hide' : 'What things cost'}
            </Button>
            <Button size="sm" variant="ghost" onClick={openLedger}>
              <History className="w-3.5 h-3.5 mr-1.5" />
              {ledger ? 'Hide history' : 'Credit history'}
            </Button>
          </div>

          {/* ── the price list: credits are only worth using if you can see
                 what an action costs before you commit to it ── */}
          {showRates && (
            <div className="mb-6 overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Action</th>
                    {TIER_ORDER.map((t) => (
                      <th key={t} className="text-right font-medium px-3 py-2">{TIER_LABELS[t]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(TYPE_LABELS).map((gt) => (
                    <tr key={gt} className="border-t border-border">
                      <td className="px-3 py-2 text-foreground">
                        {TYPE_LABELS[gt]}
                        {rates.find((r) => r.generation_type === gt)?.per_item && (
                          <span className="text-muted-foreground"> (each)</span>
                        )}
                      </td>
                      {TIER_ORDER.map((mt) => {
                        const r = rates.find((x) => x.generation_type === gt && x.model_tier === mt);
                        return (
                          <td key={mt} className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {r ? num(r.credits) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border">
                An 8-slide Pro deck costs{' '}
                {num((rates.find((r) => r.generation_type === 'presentation' && r.model_tier === 'pro')?.credits || 0) * 8)}{' '}
                credits.
              </p>
            </div>
          )}

          {ledger && (
            <div className="mb-6 rounded-xl border border-border divide-y divide-border max-h-56 overflow-y-auto">
              {ledger.length === 0 && (
                <p className="px-3 py-3 text-xs text-muted-foreground">No credit activity yet.</p>
              )}
              {ledger.map((e, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                  <div className="min-w-0">
                    <span className="text-foreground">{e.reason || e.kind}</span>
                    <span className="text-muted-foreground">
                      {' · '}
                      {new Date(e.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </span>
                  </div>
                  <span className={`tabular-nums font-medium ml-3 shrink-0 ${e.delta > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                    {e.delta > 0 ? '+' : ''}{num(e.delta)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* ── plans ── */}
          {plans.length > 0 && (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                {subscription?.status === 'active' ? 'Change plan' : 'Choose a plan'}
              </h3>
              {/* r150: annual billing (~2 months free where configured);
                  r156: hidden until an annual Stripe price actually exists */}
              {anyAnnual && (
                <div className="flex items-center gap-1 mb-3">
                  {['month', 'year'].map(iv => (
                    <button
                      key={iv}
                      onClick={() => setBillingInterval(iv)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${billingInterval === iv ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                    >
                      {iv === 'month' ? 'Monthly' : 'Annual'}
                    </button>
                  ))}
                  {billingInterval === 'year' && (
                    <span className="text-[11px] text-emerald-600 ml-1">2 months free</span>
                  )}
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                {plans.map((p) => {
                  const current = p.tier === tier;
                  const annual = billingInterval === 'year';
                  const annualReady = !!p.stripe_price_id_annual;
                  return (
                    <div
                      key={p.tier}
                      className={`rounded-xl border p-4 flex flex-col ${current ? 'border-emerald-500/60 bg-emerald-500/5' : 'border-border'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-foreground">{p.display_name}</span>
                        {current && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                      </div>
                      <div className="text-lg font-semibold text-foreground tabular-nums">
                        {annual && p.price_cents_annual
                          ? <>{money(p.price_cents_annual, p.currency)}<span className="text-xs font-normal text-muted-foreground"> /yr</span></>
                          : <>{money(p.price_cents, p.currency)}<span className="text-xs font-normal text-muted-foreground"> /mo</span></>}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 mb-3">
                        {num(p.monthly_credits)} credits / month
                      </p>
                      {Array.isArray(p.features) && p.features.length > 0 && (
                        <ul className="space-y-1 mb-3 flex-1">
                          {p.features.slice(0, 3).map((f, i) => (
                            <li key={i} className="text-[11px] text-muted-foreground flex gap-1.5">
                              <Check className="w-3 h-3 mt-0.5 shrink-0 text-emerald-500/70" />
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <Button
                        size="sm"
                        variant={current ? 'ghost' : 'default'}
                        className="w-full mt-auto"
                        disabled={current || busy === `plan:${p.tier}` || (annual && !annualReady)}
                        onClick={() => hasLiveSub
                          ? go('/api/billing/portal', {}, `plan:${p.tier}`)
                          : go('/api/billing/checkout', { tier: p.tier, interval: annual ? 'year' : 'month' }, `plan:${p.tier}`)}
                      >
                        {busy === `plan:${p.tier}`
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : current ? 'Current plan' : (annual && !annualReady) ? 'Annual soon' : hasLiveSub ? 'Switch plan' : 'Select'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── top-ups ── */}
          {packs.length > 0 && (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Top up
              </h3>
              <p className="text-[11px] text-muted-foreground mb-3">
                One-off credits that never expire. Used only after your monthly credits run out.
              </p>
              <div className="flex flex-wrap gap-2">
                {packs.map((k) => (
                  <Button
                    key={k.id}
                    size="sm" variant="outline"
                    disabled={busy === `pack:${k.id}`}
                    onClick={() => go('/api/billing/checkout', { pack_id: k.id }, `pack:${k.id}`)}
                  >
                    {busy === `pack:${k.id}`
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                    {num(k.credits)} credits · {money(k.price_cents, k.currency)}
                  </Button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* r150: Team lives INSIDE the plan page (owner decision 29/08) — the
          sidebar page was removed. Renders for exempt tiers too. */}
      <TeamPage userId={userId} embedded />
    </motion.div>
  );
}
